<template>
  <div class="LKPublicationsCard">
    <LKPublicationsCardHeader
      :id="data.id"
      :value="isActive"
      :loading="isLoading"
      :date="data.published_at"
      :slug="data.slug"
      :topic-slug="topicSlug || data.topic_slug"
      :archived="!!data.archived_at"
      :actionless="actionless"
      :authorized="authorized"
      :is-starred="isStarred"
      @input="onToggle"
      @starred="onStarred"
    />

    <v-badge
      :value="!isViewed"
      dot
      :offset-x="28"
      :offset-y="28"
      :color="color"
    >
      <div
        :class="{ 'cursor-pointer': !actionless }"
        class="LKPublicationsCard__content is-title"
        @click="!actionless && onToggle(!isActive)"
        v-text="data.name"
      />
    </v-badge>

    <LKToggleContainer :active="isActive">
      <div class="LKPublicationsCard__content has-line">
        <div
          v-if="errorMessage"
          class="LKPublicationsCard__error"
          v-text="errorMessage"
        />

        <template v-else>
          <LKPublicationsCardRecommendation
            v-if="data.has_recommendation"
            :data="data"
            :active="isActive"
          />

          <LKPublicationsCardDesc
            v-if="data.description"
            v-once
            :text="data.description"
          />

          <LKPublicationsMunMehList
            v-if="data.has_munmeh"
            :publication-id="data.id"
            :slug="data.slug"
            :active="isActive"
            class="LKPublicationsCard__mun-meh-list"
          />

          <LKPublicationsStorageList
            v-if="data.has_storage"
            :publication-id="data.id"
            :active="isActive"
            class="LKPublicationsCard__storage-list"
          />

          <LKPublicationsDataCollectionList
            v-if="data.has_data_collection"
            :publication-id="data.id"
            :active="isActive"
            class="LKPublicationsCard__data-collection-list"
          />
        </template>
      </div>
    </LKToggleContainer>
  </div>
</template>

<script>
import { lkMapper } from '@/store/modules/lk'
import variables from '@/assets/scss/exports.scss'
import { getErrorMessages } from '@/helpers/getErrorMessages'

import LKToggleContainer from '@/components/lk/LKToggleContainer.vue'
import LKPublicationsCardHeader from './LKPublicationsCardHeader.vue'
import LKPublicationsCardDesc from './LKPublicationsCardDesc.vue'
import LKPublicationsCardRecommendation from './LKPublicationsCardRecommendation.vue'
import LKPublicationsStorageList from '../LKPublicationsStorageList.vue'
import LKPublicationsDataCollectionList from '../LKPublicationsDataCollectionList.vue'
import LKPublicationsMunMehList from '../LKPublicationsMunMehList.vue'
import { rootMapper } from '@/store/modules/root'
import { LkPublicationsApi } from '@/api/resources'
import { lkPublicationsMapper } from '../../store'

export default {
  name: 'LKPublicationsCard',
  components: {
    LKToggleContainer,
    LKPublicationsCardHeader,
    LKPublicationsCardDesc,
    LKPublicationsCardRecommendation,
    LKPublicationsStorageList,
    LKPublicationsDataCollectionList,
    LKPublicationsMunMehList,
  },
  props: {
    actionless: Boolean,
    active: Boolean,
    data: {
      type: Object,
      required: true,
    },
    getPublication: {
      type: Function,
      required: true,
    },
    topicSlug: {
      type: String,
      default: '',
    },
  },
  data: (vm) => ({
    color: variables['webflow-color-blue'],
    isActive: vm.active,
    isLoading: false,
    errorMessage: null,
    isViewed: 'is_viewed' in vm.data ? vm.data.is_viewed : true,
    isStarred: !!vm.data.is_starred,
  }),
  computed: {
    ...rootMapper.mapGetters(['authorized']),
  },
  watch: {
    'data.is_starred': function onDataIsStarredChanged(value) {
      this.isStarred = !!value
    },
  },
  methods: {
    ...lkMapper.mapActions(['readPublication', 'reloadTopics']),
    ...lkMapper.mapMutations(['adjustStarredPublicationsCount']),
    ...lkPublicationsMapper.mapMutations(['setPublicationStarred', 'removeFromList']),
    async onStarred() {
      const api = new LkPublicationsApi()
      try {
        const wasStarred = this.isStarred
        if (wasStarred) {
          await api.deleteStarred(this.data.id)
        } else {
          await api.addStarred(this.data.id)
        }
        this.isStarred = !wasStarred
        this.setPublicationStarred({ id: this.data.id, isStarred: this.isStarred })
        this.adjustStarredPublicationsCount(wasStarred ? -1 : 1)
        if (wasStarred && this.topicSlug === '_starred-publications') {
          this.removeFromList(this.data.id)
        }
        this.reloadTopics().catch(() => {})
      } catch (e) {
        this.$notice.errors(e)
      }
    },
    async onToggle(value) {
      if (!value) {
        this.isActive = false
        return
      }

      if (this.data.description) {
        this.isActive = true
        return
      }

      this.errorMessage = null
      this.isLoading = true

      try {
        await this.getPublication(this.data)
        this.isViewed = await this.readPublication({
          data: this.data,
          slug: this.topicSlug || this.data.topic_slug,
        })
      } catch (e) {
        this.errorMessage = getErrorMessages(e).join(', ')
      }

      this.isActive = true
      this.isLoading = false
    },
  },
}
</script>

<style lang="scss">
@import "~@/assets/scss/components/Card/_BaseCard.scss";

.LKPublicationsCard {
  @include _BaseCard;

  &__storage-list,
  &__data-collection-list {
    margin-top: 30px;
  }
}
</style>
